import Dexie, { Table } from 'dexie';
import { signal } from '@angular/core';
import { isOfflineCapableNative } from '../commons/utils/platform';
import { AppConstants } from '../commons/app-constants';
import { OfflineTable, createStorageReady } from './storage/offline-store';
import { cancelOfflineSync } from './offline-sync-control';
import { selectFilesToRemove } from './offline-file-tidy';
export interface MasterCacheEntry {
  key: string; // e.g. 'buyers', 'suppliers', 'products', 'loadingSites', 'transportModes',
  // 'fumigationTypes' (arrays, MasterCacheService) or 'bpList'/'productList'/'loadingSiteList'
  // (Page<T> objects, ListCacheService) — shape depends on which service wrote the entry.
  data: unknown;
  fetchedAt: number; // epoch millis
}

export interface DashboardSnapshotEntry {
  key: string; // `${from}_${to}`
  stats: unknown;
  fetchedAt: number;
}

export interface DetailCacheEntry {
  key: string; // entity-type-prefixed, e.g. `tu:id:123`, `bp:encoded:abc`, `product:456`
  data: unknown;
  fetchedAt: number;
  // Last time a cached FILE (photo/document Blob) was opened from this cache — drives the file clean-up
  // below (OFFLINE_PLAN.md Phase 2). Unindexed, optional; falls back to fetchedAt.
  usedAt?: number;
}

export interface MetaEntry {
  key: string;
  value: string;
}

export type OutboxOpType = 'CREATE_TU' | 'SAVE_TALLY_DETAILS' | 'DELETE_TALLY_DETAILS' | 'CREATE_BP' | 'CREATE_PRODUCT' | 'CREATE_LOADING_SITE' | 'SAVE_TALLY_SETTINGS' | 'UPLOAD_DOCUMENT' | 'UPLOAD_PRODUCT_PHOTO' | 'CREATE_PROCESSING_RUN' | 'UPDATE_GRID_STYLE';
export type OutboxStatus = 'pending' | 'inflight' | 'synced' | 'rejected';

export interface OutboxEntry {
  seq?: number; // auto-increment PK — Dexie only allows ++ on the primary key, so this
  // (not opId) is the actual primary key; preserves creation order for replay
  opId: string; // uuid, app-level stable identifier — indexed but not the PK
  opType: OutboxOpType;
  clientUuid: string; // identifies the offline-created TU this op belongs to/depends on
  payload: unknown;
  status: OutboxStatus;
  attempts: number;
  lastError?: string;
  // Structured detail for specific terminal rejections the UI can act on beyond a generic
  // "discard" — currently only NAME_ALREADY_EXISTS (see SyncEngineService.replayLeafCreate /
  // SyncIssuesComponent's "Rename & Retry"). Unindexed — no schema version bump needed.
  errorCode?: string;
  errorMeta?: { existingId?: number; existingEncodedId?: string };
  // Extra request parameters to send on replay (e.g. SAVE_TALLY_DETAILS' overStockReason — OFFLINE_PLAN.md
  // 1.3). Unindexed, optional — no schema version bump needed.
  params?: Record<string, string>;
  // Earliest time (epoch millis) this entry may be retried after a server failure — the wait grows with
  // each failed try (OFFLINE_PLAN.md Phase 2, 2026-09-29). Unset = try now. Unindexed.
  nextAttemptAt?: number;
  createdAt: number;
}

// Rows entered offline against a still-provisional (not-yet-synced) tally master, keyed by
// clientUuid. Replayed individually as SAVE_TALLY_DETAILS outbox ops once the owning
// CREATE_TU has synced — see SyncEngineService.
export interface PendingRowsEntry {
  clientUuid: string; // PK
  rows: unknown[];
}

// Written by SyncEngineService once a CREATE_TU op successfully replays — maps every
// provisional (negative) id generated for that TU offline to the real server-assigned id, so
// dependent queued SAVE_TALLY_DETAILS ops can be rewritten before replay. Kept indefinitely
// (not cleared on logout) — same rationale as outbox/pendingRows.
export interface IdMapEntry {
  clientUuid: string; // PK
  realTuId: number;
  realEncodedId: string;
  tallyMasterIdMap: Record<number, number>; // provisional tallyMasterId -> real tallyMasterId
  // Line 1's real tallyMasterId, used as a fallback when a row's provisional tallyMasterId
  // has no exact entry in tallyMasterIdMap above — covers outbox payloads queued before the
  // _offlineProvisional* markers existed (or any other single-line TU, the common case).
  defaultTallyMasterId: number | null;
  syncedAt: number;
}

// Small key/value notes that must SURVIVE every wipe below (OFFLINE_PLAN.md, 2026-09-29):
// - `removed:<email>` — "your N offline changes were removed" notice, shown to that user on their next login;
// - `previousOwnerLease` — the previous user's signed offline lease, kept aside by OfflineLeaseService when a
//   different user's lease replaces it while that previous user's changes are still waiting (proof for the
//   server-side notice, see SyncController.removedChangesNotice);
// - `offlineLock:<email>` — the 15-day offline lock is waiting for this user to decide "sync old changes?".
export interface NoticeEntry {
  key: string;
  value: string;
}

// Offline cache — native mobile only (Android/iOS via Capacitor); never used on web (see
// isOfflineCapableNative(), which every reader/writer of this DB checks before touching it,
// so the underlying IndexedDB connection is never opened in a browser). Detail/photo/document
// caches are populated write-through (cache whatever the network returned). The last-10 master
// lists (Product/BusinessPartner/LoadingSite/TransportUnit) are instead merged in via true
// delta-sync (Increment 6) — see getLastSyncMarker/setLastSyncMarker below and
// delta-merge.util.ts's mergeDeltaIntoWindow.
class OfflineDatabase extends Dexie {
  masterCache!: Table<MasterCacheEntry, string>;
  dashboardSnapshot!: Table<DashboardSnapshotEntry, string>;
  detailCache!: Table<DetailCacheEntry, string>;
  meta!: Table<MetaEntry, string>;
  outbox!: Table<OutboxEntry, number>;
  pendingRows!: Table<PendingRowsEntry, string>;
  idMap!: Table<IdMapEntry, string>;
  notices!: Table<NoticeEntry, string>;

  constructor() {
    super('ll_offline_db');
    this.version(1).stores({
      masterCache: 'key',
      dashboardSnapshot: 'key',
      tuCache: 'key',
      meta: 'key',
    });
    // v2: tuCache renamed to detailCache — now shared by TU/BP/Product/LoadingSite detail
    // views, not just TU. Dexie handles this as a plain rename (drop old store, add new)
    // since there's no cross-version data migration need for a read-through cache.
    this.version(2).stores({
      masterCache: 'key',
      dashboardSnapshot: 'key',
      tuCache: null,
      detailCache: 'key',
      meta: 'key',
    });
    // v3: outbox (offline write queue) + pendingRows (rows entered against a not-yet-synced
    // TU) — Phase 3 offline-writes foundation.
    this.version(3).stores({
      masterCache: 'key',
      dashboardSnapshot: 'key',
      detailCache: 'key',
      meta: 'key',
      outbox: '++seq, opId, clientUuid, status',
      pendingRows: 'clientUuid',
    });
    // v4: idMap — provisional-to-real id translation table written by the sync engine as it
    // replays queued CREATE_TU ops.
    this.version(4).stores({
      masterCache: 'key',
      dashboardSnapshot: 'key',
      detailCache: 'key',
      meta: 'key',
      outbox: '++seq, opId, clientUuid, status',
      pendingRows: 'clientUuid',
      idMap: 'clientUuid',
    });
    // v5 (2026-09-29): notices — see NoticeEntry. Never cleared by any wipe below.
    this.version(5).stores({
      masterCache: 'key',
      dashboardSnapshot: 'key',
      detailCache: 'key',
      meta: 'key',
      outbox: '++seq, opId, clientUuid, status',
      pendingRows: 'clientUuid',
      idMap: 'clientUuid',
      notices: 'key',
    });
  }
}

// Phase 4 (OFFLINE_PLAN.md, 2026-09-29): `offlineDb` keeps the same tables and calls as before, but runs on the
// store chosen at start-up — this IndexedDB database, or the SQLCipher-encrypted SQLite store when switched on
// (see storage/offline-store.ts for when and how a phone moves). Nothing else in the app talks to Dexie directly.
const dexieDb = new OfflineDatabase();

/** Resolves once the store for this app start is chosen (and, the first time, moved). */
export const storageReady = createStorageReady(dexieDb);

export const offlineDb = {
  masterCache: new OfflineTable<MasterCacheEntry, string>('masterCache', storageReady),
  dashboardSnapshot: new OfflineTable<DashboardSnapshotEntry, string>('dashboardSnapshot', storageReady),
  detailCache: new OfflineTable<DetailCacheEntry, string>('detailCache', storageReady),
  meta: new OfflineTable<MetaEntry, string>('meta', storageReady),
  outbox: new OfflineTable<OutboxEntry, number>('outbox', storageReady),
  pendingRows: new OfflineTable<PendingRowsEntry, string>('pendingRows', storageReady),
  idMap: new OfflineTable<IdMapEntry, string>('idMap', storageReady),
  notices: new OfflineTable<NoticeEntry, string>('notices', storageReady),
};

// Tenant-scopes an offline cache key — defense-in-depth against the class of bug found
// 2026-08-27 (a fresh signup on a device previously used by a different real tenant briefly saw
// that tenant's stale cached business-partner data, because the ONE code path capable of wiping
// it — wipeOfflineDataIfDifferentUser above — was skipped for the whole onboarding window; see
// that incident's fix in offline-prefetch.service.ts). Every read/write cache key in this app
// (bpList/bp/productList/tuList/etc.) is otherwise a flat string keyed purely off the request
// URL — no tenant discriminator at all, so if a wipe is EVER skipped (this bug, or a future
// one), stale data lands under the exact same key the correct tenant's own screen reads from,
// indistinguishable from real data. Prefixing every key with the device's last-known tenant
// closes that off structurally, independent of whether the wipe itself ran correctly.
//
// Sourced from AppConstants.LAST_DEVICE_TENANT_ID — the exact marker wipeOfflineDataIfDifferentUser
// already maintains for this same purpose, not a new source of truth. No marker yet (a legacy
// device from before tenant tracking existed, or the brief pre-first-sync window) falls back to
// the raw unscoped key, matching today's behavior — this self-heals the first time
// wipeOfflineDataIfDifferentUser's own isUnverifiableLegacyCache branch fires, since that already
// forces a wipe whenever the marker is first established.
export function scopedKey(rawKey: string): string {
  const tenantId = localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID);
  return tenantId ? `t${tenantId}:${rawKey}` : rawKey;
}

// Clears read-only CACHE data only (masterCache/dashboardSnapshot/detailCache/meta) — never
// outbox/pendingRows/idMap, which hold real unsynced user work (e.g. a TU created offline with
// tally rows already entered) and must survive regardless of who's logging in next. Reached
// only via wipeOfflineDataIfDifferentUser() below (called by both AuthSessionGuard and
// OfflinePrefetchService), when the email logging in on this device doesn't match the last one.
// Deliberately NOT called on every logout —
// a logout by the SAME user who then logs back in must keep their cache so the next
// online-transition sync (OfflinePrefetchService) can refresh it in place instead of
// re-downloading everything from a cold cache. See wipeAllOfflineData() below for the
// different-user case, which also clears the write queue.
export async function wipeReadCache(): Promise<void> {
  if (!isOfflineCapableNative()) return;
  await Promise.all([
    offlineDb.masterCache.clear(),
    offlineDb.dashboardSnapshot.clear(),
    offlineDb.detailCache.clear(),
    offlineDb.meta.clear(),
  ]);
}

// Clears the offline WRITE queue — outbox/pendingRows/idMap. Called ONLY from
// wipeAllOfflineData() below (different-user detection) — replaying a previous user's queued
// writes under a new user's session would misattribute their offline-created data. Never call
// this alone from normal logout; exported separately only because SyncEngineService's own
// tests/callers may want the write-queue-only clear in isolation.
export async function wipeOfflineWriteQueue(): Promise<void> {
  if (!isOfflineCapableNative()) return;
  await Promise.all([
    offlineDb.outbox.clear(),
    offlineDb.pendingRows.clear(),
    offlineDb.idMap.clear(),
  ]);
}

// The only place both halves of offlineDb get cleared together — reached via
// wipeOfflineDataIfDifferentUser() below when a genuinely different user logs in on this
// device (single-flight guarded there, so this itself only ever runs once per switch even
// though multiple independent triggers can detect the switch). A different user must never
// see, or risk overwriting, a prior user's cached business data OR have a prior user's queued
// offline writes replayed under their own session.
export async function wipeAllOfflineData(): Promise<void> {
  if (!isOfflineCapableNative()) return;
  await Promise.all([wipeReadCache(), wipeOfflineWriteQueue()]);
}

/** True when a login is for a different account OR a different company than the last one used on this
 *  device (OFFLINE_PLAN.md 1.13, 2026-09-29) — the iPhone app then needs a real reload so nothing the
 *  previous account/company left in memory survives. Unknown values never count as a change. */
export function isAccountOrCompanyChange(loginEmail: string | null | undefined, loginTenantId: number | null | undefined): boolean {
  const lastEmail = localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL);
  const lastTenant = localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID);
  const otherAccount = !!lastEmail && !!loginEmail && lastEmail !== loginEmail;
  const otherCompany = !!lastTenant && loginTenantId != null && lastTenant !== String(loginTenantId);
  return otherAccount || otherCompany;
}

// ── Identity gate for the sync (OFFLINE_PLAN.md, 2026-09-29) ─────────────────────────────────
// True once wipeOfflineDataIfDifferentUser has finished for the CURRENT login. SyncEngineService never
// replays queued work before this — otherwise a sync started at app launch could send the previous
// user's changes under a new user's login before the different-user check had a chance to run.
export const offlineIdentityVerified = signal(false);

/** Called on every logout / session end — the next login must pass the identity check again. */
export function resetOfflineIdentityVerified(): void {
  offlineIdentityVerified.set(false);
  // The session is over: stop the background offline sync and hide its "Preparing offline data" strip.
  cancelOfflineSync('session-end');
}

// ── "Your offline changes were removed" (OFFLINE_PLAN.md 1.5 / D5) ─────────────────────────────
export interface RemovedChangesSummary {
  email: string;      // whose changes they were
  count: number;      // tally rows count one each; any other queued item counts as one
  kinds: string[];    // fixed codes, see SyncController.CHANGE_KIND_LABELS
  at: number;
}

const OP_KIND: Record<string, string> = {
  CREATE_TU: 'TRANSPORT_UNIT',
  SAVE_TALLY_DETAILS: 'TALLY_ROWS',
  DELETE_TALLY_DETAILS: 'TALLY_ROWS',
  CREATE_BP: 'BUSINESS_PARTNER',
  CREATE_PRODUCT: 'PRODUCT',
  CREATE_LOADING_SITE: 'LOADING_SITE',
  SAVE_TALLY_SETTINGS: 'TALLY_SETTINGS',
  UPDATE_GRID_STYLE: 'TALLY_SETTINGS',
  UPLOAD_DOCUMENT: 'DOCUMENT',
  UPLOAD_PRODUCT_PHOTO: 'DOCUMENT',
  CREATE_PROCESSING_RUN: 'PROCESSING_RUN',
};

/** Everything still unsynced in the write queue (waiting, in flight or refused), counted the way a
 *  person would: each tally row is one change. */
export async function summarizeUnsynced(): Promise<{ count: number; kinds: string[] }> {
  if (!isOfflineCapableNative()) return { count: 0, kinds: [] };
  const entries = await offlineDb.outbox.filter((e) => e.status !== 'synced').toArray();
  let count = 0;
  const kinds = new Set<string>();
  for (const e of entries) {
    count += (e.opType === 'SAVE_TALLY_DETAILS' || e.opType === 'DELETE_TALLY_DETAILS') && Array.isArray(e.payload)
      ? Math.max(1, (e.payload as unknown[]).length)
      : 1;
    kinds.add(OP_KIND[e.opType] ?? 'TALLY_ROWS');
  }
  return { count, kinds: [...kinds] };
}

// Set by SyncEngineService (it has HttpClient) — sends the server-side notice for the previous user.
// A plain hook because this file is a plain module, not an injectable.
let removedChangesReporter: ((summary: RemovedChangesSummary) => Promise<void>) | null = null;
export function setRemovedChangesReporter(fn: (summary: RemovedChangesSummary) => Promise<void>): void {
  removedChangesReporter = fn;
}

/** The notice saved for this user, if any — removed as it is read (shown once). */
export async function takeRemovedChangesNotice(email: string): Promise<RemovedChangesSummary | null> {
  if (!isOfflineCapableNative() || !email) return null;
  const key = `removed:${email}`;
  const entry = await offlineDb.notices.get(key);
  if (!entry) return null;
  await offlineDb.notices.delete(key);
  try { return JSON.parse(entry.value) as RemovedChangesSummary; } catch { return null; }
}

// Single-flight guard for wipeOfflineDataIfDifferentUser below — module-level (not per-caller)
// so AuthSessionGuard's and OfflinePrefetchService's independent calls share the SAME in-flight wipe.
// Without this, the localStorage compare-and-set alone only decides WHO triggers the wipe; the
// loser of that race would see isDifferentUser=false (the winner already updated localStorage)
// and return immediately, proceeding to read the cache while the winner's wipe is still running.
let wipeInFlight: Promise<void> | null = null;

// Shared by AuthSessionGuard (route-driven) AND OfflinePrefetchService (network-online-transition-
// driven, entirely independent of routing — see its constructor's effect()). Both can fire at
// app startup with no ordering relationship between them, so both MUST call this — and await
// it — before touching any cached list/detail data (found 2026-08-04: the mobile OAuth deep-link
// login path reloads the WebView right as the app detects it's back online, which fires both
// triggers near-simultaneously). Whichever caller's synchronous localStorage read-then-write
// runs first (JS is single-threaded, so there's no interleaving within that pair of statements)
// becomes the one that starts the wipe; every OTHER concurrent caller — including ones that,
// from their own perspective, compute no-change because the winner already flipped localStorage
// first — still awaits the SAME wipeInFlight promise before returning, so nobody downstream can
// proceed until the actual IndexedDB clear has genuinely finished.
//
// Keyed on email AND tenant, deliberately. Email alone is blind to a multi-company switch (same
// email, different tenant) — both switch call sites just reload via window.location.href='/',
// so this check ran, saw an unchanged email, and wiped nothing, leaving the PREVIOUS tenant's
// rows for the delta-merge to pile the new tenant's rows on top of. Confirmed 2026-08-04 by DB
// lookup: three tenant-1 business partners stuck in a tenant-3 session, retried endlessly.
// Because this runs on every guarded navigation against the SERVER's authoritative session
// identity (not by intercepting each switch action), any tenant change is caught on the next
// navigation no matter which feature — present or future — caused it.
//
// tenantId is null on every non-'live' sync outcome; null means "identity unknown" and never
// triggers a wipe, mirroring how email is already treated.
// onIdentityChange is an optional side-effect hook (AuthSessionGuard uses it for the account-switch
// splash message; OfflinePrefetchService has no splash context to hook into, so omits it) —
// only invoked by whichever caller actually triggers the wipe, never by one that just waits on it.
export async function wipeOfflineDataIfDifferentUser(
  newEmail: string,
  newTenantId: number | null,
  onIdentityChange?: () => void,
): Promise<void> {
  const lastEmail = localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL);
  const lastTenantRaw = localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID);

  const isDifferentUser = !!lastEmail && lastEmail !== newEmail;
  const isDifferentTenant = newTenantId !== null && !!lastTenantRaw && lastTenantRaw !== String(newTenantId);
  // A device that already has a known user but NO tenant marker cached its data before tenant
  // tracking existed — its provenance can't be verified, so the read cache is not trustworthy.
  // Deliberately NOT a full wipe: absence of a marker is not evidence of contamination, and the
  // outbox may hold genuine unsynced work from this very user. Destroying that on upgrade would
  // be a worse bug than the one being fixed.
  //
  // Gated on newTenantId !== null so this can only fire once we can actually RECORD a marker.
  // Without that guard, a frontend deployed ahead of the backend (tenantId absent from every
  // response) would find the marker perpetually missing and wipe the read cache on every single
  // navigation — a permanent cold-resync loop. With it, an old backend simply means the tenant
  // logic stays dormant and behavior is byte-for-byte what it was before this change.
  const isUnverifiableLegacyCache = newTenantId !== null && !!lastEmail && !lastTenantRaw;

  // Raw console.* (not NGXLogger — this file is a plain module, not an injectable service, and
  // wipeOfflineDataIfDifferentUser runs outside any synchronous Angular injection context) so
  // this is always visible in Safari Web Inspector / Chrome remote debugging regardless of the
  // app's own environment.prod.ts logLevel — the exact tool used to diagnose the 2026-08-27
  // cross-tenant leak incident took a full multi-hour code trace instead of a two-minute log
  // read specifically because this path logged nothing at all.
  console.info('[wipeOfflineDataIfDifferentUser] entry', JSON.stringify({
    lastEmail, lastTenantRaw, newEmail, newTenantId,
    isDifferentUser, isDifferentTenant, isUnverifiableLegacyCache,
  }));

  localStorage.setItem(AppConstants.LAST_DEVICE_USER_EMAIL, newEmail);
  if (newTenantId !== null) {
    localStorage.setItem(AppConstants.LAST_DEVICE_TENANT_ID, String(newTenantId));
  }

  if ((isDifferentUser || isDifferentTenant || isUnverifiableLegacyCache) && !wipeInFlight) {
    onIdentityChange?.();
    // Whatever the offline sync is still doing belongs to the previous user / company: stop it before the wipe.
    cancelOfflineSync('identity-change');
    // Full wipe (read cache + write queue) only for a genuine identity change — replaying one
    // tenant's queued writes under another's session would misattribute real records. Safe to
    // include the write queue here because the company switcher is blocked while anything is
    // pending/inflight (see CompanyMembershipService.buildSwitchCompanyMenuItems), so in
    // practice there is nothing to lose at this point.
    const isFullWipe = isDifferentUser || isDifferentTenant;
    const startedAt = Date.now();
    console.info('[wipeOfflineDataIfDifferentUser] starting wipe', JSON.stringify({ isFullWipe }));
    wipeInFlight = (async () => {
      // OFFLINE_PLAN.md 1.5 / D5 (2026-09-29): the previous user's unsynced changes are removed with the
      // rest of the write queue — note it first, so that user is told on their next login (on this phone
      // from the note below; anywhere else from the server-side notice). A same-email company change is
      // the same person, so only the phone note applies (the server ignores a notice to the caller).
      if (isFullWipe && lastEmail) {
        try {
          const { count, kinds } = await summarizeUnsynced();
          if (count > 0) {
            const summary: RemovedChangesSummary = { email: lastEmail, count, kinds, at: Date.now() };
            await offlineDb.notices.put({ key: `removed:${lastEmail}`, value: JSON.stringify(summary) });
            if (isDifferentUser && removedChangesReporter) {
              removedChangesReporter(summary).catch(() => { /* best-effort — the phone note still shows */ });
            }
          }
        } catch (err) {
          console.warn('[wipeOfflineDataIfDifferentUser] could not record removed changes', String(err));
        }
      }
      await (isFullWipe ? wipeAllOfflineData() : wipeReadCache());
    })()
      .then(() => {
        console.info('[wipeOfflineDataIfDifferentUser] wipe complete', JSON.stringify({ isFullWipe, elapsedMs: Date.now() - startedAt }));
      })
      .catch((err) => {
        // Best-effort, same as every other call site in this file — a failed wipe here must
        // not block login/prefetch; the next navigation re-detects the mismatch and retries.
        console.error('[wipeOfflineDataIfDifferentUser] wipe FAILED', JSON.stringify({ isFullWipe, error: String(err) }));
      })
      .finally(() => { wipeInFlight = null; });
  }

  if (wipeInFlight) {
    await wipeInFlight;
  }
  // The identity check is done for this login — the queued work (if any) now belongs to this user.
  if (newEmail) offlineIdentityVerified.set(true);
}

// ── Offline file clean-up (OFFLINE_PLAN.md Phase 2, 2026-09-29) ─────────────────────────────────
// Photos/documents are cached as Blobs in detailCache and used to pile up with no limit. Files not opened
// for OFFLINE_FILE_MAX_AGE_DAYS are removed, and the rest is kept under OFFLINE_FILE_BUDGET_BYTES by
// removing the longest-unopened first. Only FILES — the small JSON records (lists, tally rows) are kept,
// they are what offline work needs. A removed file just downloads again when opened online.
export const OFFLINE_FILE_BUDGET_BYTES = 300 * 1024 * 1024;
export const OFFLINE_FILE_MAX_AGE_DAYS = 30;

/** A cached file was just opened from the phone — remember when, so the clean-up keeps it. */
export function touchCachedFile(entry: DetailCacheEntry | undefined): void {
  if (!isOfflineCapableNative() || !entry || !(entry.data instanceof Blob)) return;
  offlineDb.detailCache.update(entry.key, { usedAt: Date.now() }).catch(() => undefined);
}

/** protectedKeys: files that must never be removed (documents of units pinned with "Keep offline"), see offline-file-tidy.ts. */
export async function tidyOfflineFiles(protectedKeys?: ReadonlySet<string>): Promise<{ removed: number; keptBytes: number }> {
  if (!isOfflineCapableNative()) return { removed: 0, keptBytes: 0 };
  const files = await offlineDb.detailCache.filter((e) => e.data instanceof Blob).toArray();
  const { remove, keptBytes } = selectFilesToRemove(
    files.map((e: DetailCacheEntry) => ({ key: e.key, size: (e.data as Blob).size || 0, lastUse: e.usedAt ?? e.fetchedAt ?? 0 })),
    {
      now: Date.now(),
      maxAgeMs: OFFLINE_FILE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000,
      budgetBytes: OFFLINE_FILE_BUDGET_BYTES,
      protectedKeys,
    },
  );
  if (remove.length) await offlineDb.detailCache.bulkDelete(remove);
  return { removed: remove.length, keptBytes };
}

// ── Delta-sync markers (Increment 6) ────────────────────────────────────────────────────────
// Per-entity, not one global marker — the batch runs concurrently and any one leg can fail
// independently, so each area needs to decide on its own whether it's safe to trust a delta
// next time. Stored in the `meta` table (existed since Phase 2 but was never actually written
// until now). Value is the SERVER's response time (X-Server-Time header), never the client's
// own clock — avoids missing/double-fetching rows right at the boundary from device/server
// clock skew.
function lastSyncMetaKey(entity: string): string {
  return scopedKey(`lastSyncAt:${entity}`);
}

export async function getLastSyncMarker(entity: string): Promise<string | null> {
  if (!isOfflineCapableNative()) return null;
  const entry = await offlineDb.meta.get(lastSyncMetaKey(entity));
  return entry?.value ?? null;
}

export async function setLastSyncMarker(entity: string, serverTime: string): Promise<void> {
  if (!isOfflineCapableNative()) return;
  await offlineDb.meta.put({ key: lastSyncMetaKey(entity), value: serverTime });
}

// ── Session snapshot (permissions + config, for offline guard enforcement) ─────────────────
// Written by PermissionService.syncSessionForGuard() on every successful live
// /users/me/permissions load; read by the same method as a fallback when that live call fails
// for a genuine network reason (AuthSessionGuard runs this on every guarded navigation). Deliberately
// NOT wiped by wipeReadCache() alone — it's cleared as part of wipeAllOfflineData() like every
// other read cache, same as everything else in `meta`. A missing snapshot (never synced even
// once) is the signal AuthSessionGuard uses to refuse to let the device proceed at all.
const SESSION_SNAPSHOT_KEY = 'sessionSnapshot';

export interface SessionSnapshot {
  permissions: string[];
  rbacEnabled: boolean;
  accessRoleName: string | null;
  maxOfflineDays: number;
  syncedAt: number; // epoch millis — when this snapshot was captured from a genuinely live load
}

export async function getSessionSnapshot(): Promise<SessionSnapshot | null> {
  if (!isOfflineCapableNative()) return null;
  const entry = await offlineDb.meta.get(SESSION_SNAPSHOT_KEY);
  if (!entry?.value) return null;
  try {
    return JSON.parse(entry.value) as SessionSnapshot;
  } catch {
    return null;
  }
}

export async function setSessionSnapshot(snapshot: SessionSnapshot): Promise<void> {
  if (!isOfflineCapableNative()) return;
  await offlineDb.meta.put({ key: SESSION_SNAPSHOT_KEY, value: JSON.stringify(snapshot) });
}
